import { useState, useRef, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  Mic, MicOff, Square, Loader2, FileText, AlertTriangle,
  Globe, Clock, Shield, Activity, Radio
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANG_MAP = {
  'English': 'en-IN', 'Hindi': 'hi-IN', 'Tamil': 'ta-IN', 'Telugu': 'te-IN',
  'Kannada': 'kn-IN', 'Malayalam': 'ml-IN', 'Bengali': 'bn-IN', 'Marathi': 'mr-IN',
  'Gujarati': 'gu-IN', 'Punjabi': 'pa-IN', 'Odia': 'od-IN', 'Assamese': 'as-IN',
  'Urdu': 'ur-IN', 'Nepali': 'ne-IN',
};

const MAX_RECORDING_SEC = 300; // 5 minutes

export default function AmbientScribe({ open, onClose, onResult }) {
  const [language, setLanguage] = useState('English');
  const [patientAge, setPatientAge] = useState('');
  const [patientSex, setPatientSex] = useState('');

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcript, setTranscript] = useState('');
  const [isTranscribing, setIsTranscribing] = useState(false);

  // SOAP generation state
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState('');
  const [error, setError] = useState(null);

  // Usage
  const [usage, setUsage] = useState(null);

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Load usage on open
  const loadUsage = useCallback(async () => {
    try {
      const res = await fetch(`${API}/ambient/status`);
      const data = await res.json();
      setUsage(data);
    } catch (err) {
      // Usage status is informational only; log for production debugging
      console.warn('[AmbientScribe] Usage status fetch failed:', err?.message || err);
    }
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    setError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        clearInterval(timerRef.current);
        setIsRecording(false);

        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        if (blob.size < 1000) {
          setError('Recording too short. Speak for at least a few seconds.');
          return;
        }

        // Transcribe via Sarvam
        setIsTranscribing(true);
        const langCode = LANG_MAP[language] || 'en-IN';
        const formData = new FormData();
        formData.append('file', blob, 'ambient.webm');
        formData.append('language_code', langCode);

        try {
          const res = await fetch(`${API}/ambient/transcribe`, { method: 'POST', body: formData });
          const data = await res.json();
          if (data.transcript) {
            setTranscript(prev => prev ? prev + '\n\n' + data.transcript : data.transcript);
          } else {
            setError(data.error || 'Transcription failed');
          }
        } catch (err) {
          setError('Transcription network error. Try again.');
        }
        setIsTranscribing(false);
      };

      setIsRecording(true);
      setRecordingTime(0);
      mediaRecorder.start(5000); // Collect in 5-second chunks

      // Timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => {
          if (prev >= MAX_RECORDING_SEC - 1) {
            mediaRecorder.stop();
            return MAX_RECORDING_SEC;
          }
          return prev + 1;
        });
      }, 1000);

    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Microphone blocked. Allow mic access in browser settings.');
      } else {
        setError('Could not access microphone: ' + err.message);
      }
    }
  }, [language]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  // Generate SOAP from transcript
  const generateSoap = useCallback(async () => {
    if (!transcript.trim()) {
      setError('No transcript available. Record a conversation first.');
      return;
    }
    setIsGenerating(true);
    setError(null);

    setGenerationStep('Structuring conversation with Claude...');
    try {
      const res = await fetch(`${API}/ambient/generate-soap`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transcript: transcript,
          age: patientAge,
          sex: patientSex,
          language: language,
        }),
      });

      setGenerationStep('Running clinical pipeline...');
      const data = await res.json();

      if (data.encounterId) {
        setGenerationStep('Complete!');
        onResult(data);
        // Reset
        setTranscript('');
        setRecordingTime(0);
        setPatientAge('');
        setPatientSex('');
        loadUsage();
      } else {
        setError(data.error || 'SOAP generation failed');
      }
    } catch (err) {
      setError('Network error during SOAP generation.');
    }
    setIsGenerating(false);
    setGenerationStep('');
  }, [transcript, patientAge, patientSex, language, onResult, loadUsage]);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) { if (isRecording) stopRecording(); onClose(); } }}>
      <DialogContent className="max-w-2xl max-h-[90vh]" data-testid="ambient-scribe-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <Radio className="w-5 h-5 text-[#EF4444]" />
            Ambient Clinical Scribe
            <Badge className="bg-[#EF4444] text-white text-[10px] ml-2">BETA</Badge>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[75vh] pr-2">
          <div className="space-y-4 p-1">
            {/* Info Banner */}
            <div className="bg-sky-50 border border-sky-200 rounded-xl p-3 text-sm" data-testid="ambient-info">
              <p className="font-medium text-sky-900 mb-1">Record your doctor-patient conversation</p>
              <p className="text-xs text-sky-700">Audio is transcribed via Sarvam AI, then structured by Claude into clinical sections and processed through the deterministic pipeline for SOAP notes and risk flags.</p>
            </div>

            {/* Usage Stats */}
            {usage && (
              <div className="flex items-center gap-3 text-xs text-slate-500" data-testid="ambient-usage">
                <Shield className="w-3.5 h-3.5" />
                <span>Today: {usage.user_used}/{usage.user_limit} sessions used</span>
                <span className="text-slate-300">|</span>
                <span>System: {usage.global_used}/{usage.global_limit}</span>
              </div>
            )}

            {/* Patient Demographics */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs font-bold">Patient Age</Label>
                <Input
                  value={patientAge}
                  onChange={e => setPatientAge(e.target.value)}
                  placeholder="e.g., 55"
                  className="mt-1 h-9 text-sm border-2 border-slate-300 rounded-lg"
                  data-testid="ambient-age"
                />
              </div>
              <div>
                <Label className="text-xs font-bold">Sex</Label>
                <Select value={patientSex} onValueChange={setPatientSex}>
                  <SelectTrigger className="mt-1 h-9 text-sm border-2 border-slate-300 rounded-lg" data-testid="ambient-sex">
                    <SelectValue placeholder="Select" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Male">Male</SelectItem>
                    <SelectItem value="Female">Female</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold flex items-center gap-1"><Globe className="w-3 h-3" /> Language</Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="mt-1 h-9 text-sm border-2 border-slate-300 rounded-lg" data-testid="ambient-language">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.keys(LANG_MAP).map(l => (
                      <SelectItem key={l} value={l}>{l}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Recording Controls */}
            <div className="bg-slate-50 rounded-xl p-4 border border-slate-200" data-testid="recording-controls">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  {isRecording ? (
                    <div className="flex items-center gap-2">
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-ping" />
                      <span className="text-sm font-bold text-red-700">Recording</span>
                    </div>
                  ) : isTranscribing ? (
                    <div className="flex items-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                      <span className="text-sm font-medium text-blue-700">Transcribing...</span>
                    </div>
                  ) : (
                    <span className="text-sm text-slate-500">Ready to record</span>
                  )}
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-3.5 h-3.5 text-slate-400" />
                  <span className={`font-mono ${recordingTime > 240 ? 'text-red-600 font-bold' : 'text-slate-600'}`}>
                    {formatTime(recordingTime)} / {formatTime(MAX_RECORDING_SEC)}
                  </span>
                </div>
              </div>

              <div className="flex gap-2">
                {!isRecording ? (
                  <Button
                    onClick={() => { loadUsage(); startRecording(); }}
                    disabled={isTranscribing || isGenerating}
                    className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-lg"
                    data-testid="ambient-record-btn"
                  >
                    <Mic className="w-4 h-4 mr-2" />
                    {transcript ? 'Continue Recording' : 'Start Recording'}
                  </Button>
                ) : (
                  <Button
                    onClick={stopRecording}
                    className="flex-1 bg-slate-800 hover:bg-slate-900 text-white rounded-lg"
                    data-testid="ambient-stop-btn"
                  >
                    <Square className="w-4 h-4 mr-2" />
                    Stop Recording
                  </Button>
                )}
              </div>

              {isRecording && (
                <div className="mt-2 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-red-500 transition-all duration-1000"
                    style={{ width: `${(recordingTime / MAX_RECORDING_SEC) * 100}%` }}
                  />
                </div>
              )}
            </div>

            {/* Transcript */}
            {transcript && (
              <div data-testid="ambient-transcript-section">
                <Label className="text-xs font-bold mb-1 block">Conversation Transcript</Label>
                <Textarea
                  value={transcript}
                  onChange={e => setTranscript(e.target.value)}
                  className="min-h-[120px] text-sm border-2 border-slate-300 rounded-lg font-[Manrope]"
                  placeholder="Transcript will appear here after recording..."
                  data-testid="ambient-transcript"
                />
                <p className="text-[10px] text-slate-400 mt-1">You can edit the transcript before generating the SOAP note.</p>
              </div>
            )}

            {/* Or paste transcript manually */}
            {!transcript && !isRecording && (
              <div data-testid="ambient-paste-section">
                <Label className="text-xs font-bold mb-1 block">Or paste a conversation transcript</Label>
                <Textarea
                  value={transcript}
                  onChange={e => setTranscript(e.target.value)}
                  className="min-h-[100px] text-sm border-2 border-slate-300 rounded-lg"
                  placeholder="Doctor: What brings you in today?&#10;Patient: I've been having chest pain for the past 3 days...&#10;Doctor: Can you describe the pain?..."
                  data-testid="ambient-manual-input"
                />
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2" data-testid="ambient-error">
                <AlertTriangle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm text-red-800">{error}</p>
                  <button onClick={() => setError(null)} className="text-xs text-red-500 hover:underline mt-1">Dismiss</button>
                </div>
              </div>
            )}

            {/* Generate SOAP Button */}
            {transcript && (
              <Button
                onClick={generateSoap}
                disabled={isGenerating || isRecording || isTranscribing || !transcript.trim()}
                className="w-full bg-[#28a745] hover:bg-green-700 text-white rounded-lg py-3 text-base font-medium"
                data-testid="ambient-generate-btn"
              >
                {isGenerating ? (
                  <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> {generationStep || 'Processing...'}</>
                ) : (
                  <><Activity className="w-5 h-5 mr-2" /> Generate SOAP Note from Conversation</>
                )}
              </Button>
            )}

            {/* Safety Disclaimer */}
            <div className="text-[10px] text-slate-400 space-y-1 border-t border-slate-100 pt-3" data-testid="ambient-disclaimer">
              <p className="font-medium">Clinical Safety Notice:</p>
              <p>Ambient scribe output is a DRAFT requiring physician review. The system structures conversation content — it does not diagnose or prescribe. All risk flags are generated by deterministic clinical engines, not AI inference.</p>
              <p>Recording is processed via Sarvam AI (STT) and structured via Claude. Audio is not stored permanently.</p>
              <div className="flex items-center gap-3 mt-1 text-slate-500">
                <span>Max recording: 5 min</span>
                <span>|</span>
                <span>Daily limit: {usage?.user_limit || 10} sessions</span>
                <span>|</span>
                <span>Cost-controlled</span>
              </div>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
